import React, { useEffect } from 'react';
import { MealBreakConfigForm } from '@/components/config_forms/meal_break/MealBreakConfigForm';
import type { ClientFormData } from '../new_client/utilities/formDataUtils';
import type { MealBreakConfig } from '@/types';
import type { ValidationError } from '@/utils/validation/types';
import { getFieldErrorElement } from '@/utils/validation/helpers';
import { toast } from 'sonner';

interface ClientMealBreakConfigProps {
  formData: ClientFormData;
  handleChange: (field: keyof ClientFormData, value: ClientFormData[keyof ClientFormData]) => void;
  handleSwitchChange: (field: keyof ClientFormData, checked: boolean) => void;
  onFieldBlur?: (field: keyof ClientFormData) => void;
  isLoading?: boolean;
  isCustomized?: boolean;
  onRevert?: () => void;
  granularCustomizedFields?: Record<string, boolean>;
  errors?: ValidationError[];
  touchedFields?: Set<string>;
  noCard?: boolean;
  hideTitle?: boolean;
  hideRevert?: boolean;
  hideToggle?: boolean;
}

const roundToNearest5 = (value: number | null): number | null => {
  if (value === null || isNaN(value)) return null;
  return Math.round(value / 5) * 5;
};

export const ClientMealBreakConfig: React.FC<ClientMealBreakConfigProps> = ({
  formData,
  handleChange,
  handleSwitchChange,
  onFieldBlur,
  isLoading,
  isCustomized,
  onRevert,
  granularCustomizedFields = {},
  errors = [],
  touchedFields = new Set(),
  noCard,
  hideTitle,
  hideRevert,
  hideToggle,
}) => {
  const mealConfig: MealBreakConfig = {
    applyMealSurcharge: formData.apply_meal_surcharge ?? false,
    mealDeadlineHours: formData.meal_deadline_hours ?? null,
    recurringMealDeadline: formData.recurring_meal_deadline ?? false,
    mealSurchargeType: formData.meal_surcharge_type as MealBreakConfig['mealSurchargeType'],
    useAdditionalOffset: formData.use_additional_offset ?? true,
    creditIntervalMinutes: formData.credit_interval_minutes ?? null,
    timeCreditMinutes: formData.time_credit_minutes ?? null,
    stepUpAfterMinutes: formData.step_up_after_minutes ?? null,
    fixedFeeSchedule: formData.fixed_fee_schedule as MealBreakConfig['fixedFeeSchedule'],
    fixedFeeAmount: formData.fixed_fee_amount ?? null,
    premiumType: formData.meal_premium_type as MealBreakConfig['premiumType'],
    premiumValue: formData.meal_premium_value ?? null,
    premiumBasis: (formData.meal_premium_basis as string) ?? 'STRAIGHT_TIME',
    premiumStacking: ((formData as Record<string, unknown>).meal_premium_stacking as MealBreakConfig['premiumStacking']) ?? 'flat',
    applyOnHalfDays: formData.meal_apply_on_half_days ?? true,
    mealBreakLengthMinutes: formData.meal_break_length_minutes ?? null,
    breakBillingPolicy: formData.break_billing_policy as MealBreakConfig['breakBillingPolicy'],
    gracePeriodMinutes: formData.grace_period_minutes ?? null,
    // Unified rate-offset fields (Phase 9). Map from the flattened client-form
    // shape; fall back to DB-safe defaults so the CHECK constraint holds for
    // methods that don't use these fields.
    floorSource: ((formData as Record<string, unknown>).meal_floor_source as MealBreakConfig['floorSource']) ?? 'none',
    overtimeOffset: ((formData as Record<string, unknown>).meal_overtime_offset as number | undefined) ?? 0,
    floorTierCode: ((formData as Record<string, unknown>).meal_floor_tier_code as string | null | undefined) ?? null,
    floorTierCustomValue: ((formData as Record<string, unknown>).meal_floor_tier_custom_value as number | null | undefined) ?? null,
    extendedOvertimeEnabled: formData.meal_extended_overtime_enabled ?? false,
    extendedOvertimeTiers: ((formData as Record<string, unknown>).meal_extended_overtime_tiers as MealBreakConfig['extendedOvertimeTiers']) ?? [],
    mealTriggerMode: (formData.meal_trigger_mode as MealBreakConfig['mealTriggerMode']) ?? 'deadline',
    requiredBreaks: formData.required_breaks ?? null,
    thresholdHours: formData.threshold_hours ?? null,
    additionalBreakIntervalHours: formData.additional_break_interval_hours ?? null,
    requiredBreaksHalfDay: formData.required_breaks_half_day ?? null,
  };

  const mealConfigCustomized = {
    applyMealSurcharge: granularCustomizedFields.apply_meal_surcharge,
    mealDeadlineHours: granularCustomizedFields.meal_deadline_hours,
    recurringMealDeadline: granularCustomizedFields.recurring_meal_deadline,
    mealSurchargeType: granularCustomizedFields.meal_surcharge_type,
    useAdditionalOffset: granularCustomizedFields.use_additional_offset,
    creditIntervalMinutes: granularCustomizedFields.credit_interval_minutes,
    timeCreditMinutes: granularCustomizedFields.time_credit_minutes,
    fixedFeeSchedule: granularCustomizedFields.fixed_fee_schedule,
    stepUpAfterMinutes: granularCustomizedFields.step_up_after_minutes,
    premiumType: granularCustomizedFields.meal_premium_type,
    premiumValue: granularCustomizedFields.meal_premium_value,
    premiumBasis: granularCustomizedFields.meal_premium_basis,
    premiumStacking: (granularCustomizedFields as Record<string, boolean | undefined>).meal_premium_stacking,
    applyOnHalfDays: granularCustomizedFields.meal_apply_on_half_days,
    mealBreakLengthMinutes: granularCustomizedFields.meal_break_length_minutes,
    breakBillingPolicy: granularCustomizedFields.break_billing_policy,
    gracePeriodMinutes: granularCustomizedFields.grace_period_minutes,
    extendedOvertimeEnabled: granularCustomizedFields.meal_extended_overtime_enabled,
    extendedOvertimeTiers: granularCustomizedFields.meal_extended_overtime_tiers,
    mealTriggerMode: granularCustomizedFields.meal_trigger_mode,
    requiredBreaks: granularCustomizedFields.required_breaks,
    thresholdHours: granularCustomizedFields.threshold_hours,
    additionalBreakIntervalHours: granularCustomizedFields.additional_break_interval_hours,
    requiredBreaksHalfDay: granularCustomizedFields.required_breaks_half_day,
  };

  const onMealConfigChange = (updates: Partial<MealBreakConfig>) => {
    // If surcharge type is being set to hourly_premium and premiumType isn't already set, default it.
    if (updates.mealSurchargeType === 'hourly_premium' && !formData.meal_premium_type) {
      handleChange('meal_premium_type', 'fixed');
    }

    // Map updates back to the flattened formData structure
    if (updates.applyMealSurcharge !== undefined) handleSwitchChange('apply_meal_surcharge', updates.applyMealSurcharge);
    if (updates.mealDeadlineHours !== undefined) handleChange('meal_deadline_hours', updates.mealDeadlineHours);
    if (updates.mealBreakLengthMinutes !== undefined) handleChange('meal_break_length_minutes', updates.mealBreakLengthMinutes);
    if (updates.breakBillingPolicy !== undefined) handleChange('break_billing_policy', updates.breakBillingPolicy);
    if (updates.recurringMealDeadline !== undefined) handleSwitchChange('recurring_meal_deadline', updates.recurringMealDeadline);
    if (updates.mealSurchargeType !== undefined) handleChange('meal_surcharge_type', updates.mealSurchargeType);
    if (updates.useAdditionalOffset !== undefined) handleSwitchChange('use_additional_offset', updates.useAdditionalOffset);
    if (updates.creditIntervalMinutes !== undefined) handleChange('credit_interval_minutes', updates.creditIntervalMinutes);
    if (updates.timeCreditMinutes !== undefined) handleChange('time_credit_minutes', updates.timeCreditMinutes);
    if (updates.stepUpAfterMinutes !== undefined) handleChange('step_up_after_minutes', updates.stepUpAfterMinutes);
    if (updates.fixedFeeSchedule !== undefined) handleChange('fixed_fee_schedule', updates.fixedFeeSchedule);
    if (updates.fixedFeeAmount !== undefined) handleChange('fixed_fee_amount', updates.fixedFeeAmount);
    if (updates.premiumType !== undefined) handleChange('meal_premium_type', updates.premiumType);
    if (updates.premiumValue !== undefined) handleChange('meal_premium_value', updates.premiumValue);
    if (updates.premiumBasis !== undefined) handleChange('meal_premium_basis', updates.premiumBasis);
    if (updates.premiumStacking !== undefined) handleChange('meal_premium_stacking' as keyof typeof formData, updates.premiumStacking);
    if (updates.applyOnHalfDays !== undefined) handleSwitchChange('meal_apply_on_half_days', updates.applyOnHalfDays);
    // Unified rate-offset fields (Phase 9). Cast via keyof extension so the
    // flat client-form handler accepts the unified column names.
    if (updates.floorSource !== undefined) handleChange('meal_floor_source' as keyof typeof formData, updates.floorSource);
    if (updates.overtimeOffset !== undefined) handleChange('meal_overtime_offset' as keyof typeof formData, updates.overtimeOffset);
    if (updates.floorTierCode !== undefined) handleChange('meal_floor_tier_code' as keyof typeof formData, updates.floorTierCode);
    if (updates.floorTierCustomValue !== undefined) handleChange('meal_floor_tier_custom_value' as keyof typeof formData, updates.floorTierCustomValue);
    if (updates.mealTriggerMode !== undefined) handleChange('meal_trigger_mode', updates.mealTriggerMode);
    if (updates.requiredBreaks !== undefined) handleChange('required_breaks', updates.requiredBreaks);
    if (updates.thresholdHours !== undefined) handleChange('threshold_hours', updates.thresholdHours);
    if (updates.additionalBreakIntervalHours !== undefined) handleChange('additional_break_interval_hours', updates.additionalBreakIntervalHours);
    if (updates.requiredBreaksHalfDay !== undefined) handleChange('required_breaks_half_day', updates.requiredBreaksHalfDay);
    // Extended overtime fields — required for the count + offset_overtime
    // mode-switch clearing path (see MealBreakConfigForm.tsx and
    // BaseSettingsSection.tsx getSurchargeTypeSwap). Without these mappings
    // the count_mode_offset_no_escalation_check rejects the save.
    if (updates.extendedOvertimeEnabled !== undefined) {
      handleSwitchChange('meal_extended_overtime_enabled' as keyof typeof formData, updates.extendedOvertimeEnabled);
    }
    if (updates.extendedOvertimeTiers !== undefined) {
      handleChange(
        'meal_extended_overtime_tiers' as keyof typeof formData,
        updates.extendedOvertimeTiers as unknown as Parameters<typeof handleChange>[1]
      );
    }
  };

  const hasNonStraightTimeMultipliers = (formData.rate_multipliers || []).some(m => m.code !== 'STRAIGHT_TIME');

  useEffect(() => {
    if (mealConfig.applyMealSurcharge && mealConfig.mealSurchargeType === 'offset_overtime' && !hasNonStraightTimeMultipliers) {
      onMealConfigChange({ mealSurchargeType: null });
      toast.info('Meal surcharge method reset: "Offset Overtime" is unavailable without overtime multipliers.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onMealConfigChange is defined in render scope
  }, [hasNonStraightTimeMultipliers, mealConfig.applyMealSurcharge, mealConfig.mealSurchargeType]);

  const handleRoundingBlur = (field: keyof ClientFormData) => {
    const value = formData[field];
    const numValue = value != null && value !== '' ? Number(value) : null;

    if (numValue !== null && !isNaN(numValue)) {
      const rounded = roundToNearest5(numValue);
      if (rounded !== numValue) {
        handleChange(field, rounded);
      }
    }
    onFieldBlur?.(field);
  };

  return (
    <MealBreakConfigForm
      title={hideTitle ? "" : "Client-Specific Meal Break Rules"}
      description={hideTitle ? "" : "Override default meal break settings for this client."}
      mealConfig={mealConfig}
      onMealConfigChange={onMealConfigChange}
      context="client"
      isCustomized={isCustomized}
      onRevert={hideRevert ? undefined : onRevert}
      mealConfigCustomized={mealConfigCustomized}
      noCard={noCard}
      roles={formData.roles || []}
      rateMultipliers={formData.rate_multipliers || []}
      minimumBillableHours={formData.minimum_billable_hours}
      halfDayHours={formData.half_day_hours}
      mealDeadlineHoursError={getFieldErrorElement('meal_deadline_hours', errors, touchedFields)}
      gracePeriodMinutesError={getFieldErrorElement('grace_period_minutes', errors, touchedFields)}
      creditIntervalMinutesError={getFieldErrorElement('credit_interval_minutes', errors, touchedFields)}
      stepUpAfterMinutesError={getFieldErrorElement('step_up_after_minutes', errors, touchedFields)}
      timeCreditMinutesError={getFieldErrorElement('time_credit_minutes', errors, touchedFields)}
      mealDeadlineHoursProps={{
        value: formData.meal_deadline_hours ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('meal_deadline_hours', e.target.value ? parseFloat(e.target.value) : null),
        id: 'meal_deadline_hours',
        onBlur: () => onFieldBlur?.('meal_deadline_hours'),
        disabled: isLoading,
        'data-validation-key': 'meal_deadline_hours',
      }}
      creditIntervalMinutesProps={{
        value: formData.credit_interval_minutes ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('credit_interval_minutes', e.target.value ? parseFloat(e.target.value) : null),
        id: 'credit_interval_minutes',
        onBlur: () => handleRoundingBlur('credit_interval_minutes'),
        disabled: isLoading,
        'data-validation-key': 'credit_interval_minutes',
      }}
      stepUpAfterMinutesProps={{
        value: formData.step_up_after_minutes ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('step_up_after_minutes', e.target.value ? parseFloat(e.target.value) : null),
        id: 'escalation-threshold-minutes',
        onBlur: () => onFieldBlur?.('step_up_after_minutes'),
        disabled: isLoading,
        'data-validation-key': 'step_up_after_minutes',
      }}
      timeCreditMinutesProps={{
        value: formData.time_credit_minutes ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('time_credit_minutes', e.target.value ? parseFloat(e.target.value) : null),
        id: 'time_credit_minutes',
        onBlur: () => onFieldBlur?.('time_credit_minutes'),
        disabled: isLoading,
        'data-validation-key': 'time_credit_minutes',
      }}
      premiumValueProps={{
        value: formData.meal_premium_value ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('meal_premium_value', e.target.value ? parseFloat(e.target.value) : null),
        id: 'meal_premium_value',
        onBlur: () => onFieldBlur?.('meal_premium_value'),
        disabled: isLoading,
        'data-validation-key': 'meal_premium_value',
      }}
      premiumValueError={getFieldErrorElement('meal_premium_value', errors, touchedFields)}
      fixedFeeAmountProps={{
        value: formData.fixed_fee_amount ?? '',
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => handleChange('fixed_fee_amount', e.target.value ? parseFloat(e.target.value) : null),
        id: 'fixed_fee_amount',
        onBlur: () => onFieldBlur?.('fixed_fee_amount'),
        disabled: isLoading,
        'data-validation-key': 'fixed_fee_amount',
      }}
      fixedFeeAmountError={getFieldErrorElement('fixed_fee_amount', errors, touchedFields)}
      hideToggle={hideToggle}
      extendedOvertimeEnabled={formData.meal_extended_overtime_enabled ?? false}
      onExtendedOvertimeEnabledChange={(v) => handleChange('meal_extended_overtime_enabled' as keyof ClientFormData, v)}
      extendedOvertimeTiers={(formData as Record<string, unknown>).meal_extended_overtime_tiers as MealBreakConfig['extendedOvertimeTiers'] ?? []}
      onExtendedOvertimeTiersChange={(tiers) => handleChange('meal_extended_overtime_tiers' as keyof ClientFormData, tiers as unknown as ClientFormData[keyof ClientFormData])}
    />
  );
};
